import React, { useRef, useLayoutEffect, useState, useCallback, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDateTimeDisplay } from '../../../../utils/base';

const WeeklyChart = ({
    data = [],
    maxValue = null,
    color = null,
    height = 50,
    barWidth = 10,
    gap = 5,
    showYAxis = true,
    yAxisLabels = [],
    visibleItems = 7, // 화면에 표시할 바 개수
    startFromEnd = false, // 끝에서부터 시작할지 여부
    targetValue = 20000, // 목표값 설정
    isRangeData = false, // 심박수 범위 데이터인지 여부
    onDateSelect = null, // 날짜 선택 콜백 함수
    onScrollToEnd = null, // 스크롤 끝에서 콜백 함수
    enableWeekSwipe = false, // 주간 스와이프 활성화
    onWeekChange = null, // 주 변경 콜백 함수 (delta: -1 또는 1)
    selectedDate = null, // 현재 선택된 날짜 (주 변경 시 사용)
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const scrollContainerRef = useRef(null);
    const containerRef = useRef(null); // 최상위 컨테이너 ref 추가
    const [selectedDateIndex, setSelectedDateIndex] = useState(null);

    // enableWeekSwipe=false(일간 뷰 등)일 때: 마우스 드래그로 횡스크롤 + 텍스트 선택 방지
    const dragScrollRef = useRef({
        isDown: false,
        startX: 0,
        startScrollLeft: 0,
        moved: false,
    });
    const [isDragScrolling, setIsDragScrolling] = useState(false);

    const clearTextSelection = useCallback(() => {
        const selection = window.getSelection?.();
        if (selection && selection.type !== 'None') {
            selection.removeAllRanges();
        }
    }, []);

    const handleDragScrollMouseDown = useCallback((e) => {
        if (enableWeekSwipe) return;
        if (e.button !== 0) return;
        if (!scrollContainerRef.current) return;

        dragScrollRef.current.isDown = true;
        dragScrollRef.current.startX = e.clientX;
        dragScrollRef.current.startScrollLeft = scrollContainerRef.current.scrollLeft;
        dragScrollRef.current.moved = false;
        setIsDragScrolling(true);

        if (e.cancelable) e.preventDefault();
        clearTextSelection();
    }, [enableWeekSwipe, clearTextSelection]);

    const handleDragScrollMouseMove = useCallback((e) => {
        if (enableWeekSwipe) return;
        if (!dragScrollRef.current.isDown) return;
        if (!scrollContainerRef.current) return;

        const dx = e.clientX - dragScrollRef.current.startX;
        if (Math.abs(dx) > 3) {
            dragScrollRef.current.moved = true;
        }
        scrollContainerRef.current.scrollLeft = dragScrollRef.current.startScrollLeft - dx;
        clearTextSelection();
        if (e.cancelable) e.preventDefault();
    }, [enableWeekSwipe, clearTextSelection]);

    const handleDragScrollMouseUp = useCallback(() => {
        if (!dragScrollRef.current.isDown) return;
        dragScrollRef.current.isDown = false;
        setIsDragScrolling(false);
    }, []);

    useEffect(() => {
        if (enableWeekSwipe) return;
        if (!isDragScrolling) return;

        const onMove = (e) => handleDragScrollMouseMove(e);
        const onUp = () => handleDragScrollMouseUp();

        document.addEventListener('mousemove', onMove, { passive: false });
        document.addEventListener('mouseup', onUp);

        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [enableWeekSwipe, isDragScrolling, handleDragScrollMouseMove, handleDragScrollMouseUp]);

    // Y축 라벨 위치 초기값 계산
    const chartContainerWidth = data.length >= visibleItems
        ? visibleItems * (barWidth + gap)
        : (data.length * (barWidth + gap) - gap);
    const chartContainerMarginRight = data.length >= visibleItems ? 30 : 0;
    const isCentered = data.length <= visibleItems;
    const initialYAxisRight = isCentered
        ? 0 // useLayoutEffect에서 계산
        : chartContainerMarginRight + (barWidth + gap);

    const [yAxisRight, setYAxisRight] = useState(initialYAxisRight); // Y축 라벨 위치 상태
    const [gridLineWidth, setGridLineWidth] = useState(chartContainerWidth); // 그리드 라인 너비 상태

    // 전역 이벤트 핸들러를 ref로 저장하여 useEffect 의존성 문제 해결
    const handleMouseDownRef = useRef(null);
    const handleMouseMoveRef = useRef(null);
    const handleMouseUpRef = useRef(null);
    const handleTouchStartRef = useRef(null);
    const handleTouchMoveRef = useRef(null);
    const handleTouchEndRef = useRef(null);
    const onWeekChangeRef = useRef(onWeekChange);

    // onWeekChange ref 업데이트
    useEffect(() => {
        onWeekChangeRef.current = onWeekChange;
    }, [onWeekChange]);

    // 주간 스와이프를 위한 ref
    const swipeRef = useRef({
        startX: 0,
        startY: 0,
        endX: 0,
        endY: 0,
        isVerticalScroll: false,
        hasMoved: false,
        isClick: false,
        isMouseDown: false,
        isBarClick: false,
        touchStartX: 0,
        touchStartY: 0,
        touchStartTime: 0,
        dateToUse: null // 사용할 날짜 저장
    });

    // 스크롤 상태 추적을 위한 ref
    const scrollRef = useRef({
        isScrolling: false,
        scrollEndTime: 0
    });

    // 색상 설정
    const barColor = color || theme.palette.figma.steps;

    const chartHeight = 44;
    const rangeDataMin = 40;
    const rangeDataMax = 140;

    // 바 클릭 핸들러
    const handleBarClick = (index, item, e) => {
        // 스크롤 중이거나 스크롤이 끝난 직후(300ms 이내)인 경우 클릭 무시
        const now = Date.now();
        if (scrollRef.current.isScrolling || (now - scrollRef.current.scrollEndTime) < 300) {
            return;
        }

        // 스와이프가 활성화되어 있고 클릭이 감지된 경우 스와이프 처리 무시
        if (enableWeekSwipe && swipeRef.current.isMouseDown) {
            swipeRef.current.isClick = true;
            swipeRef.current.isMouseDown = false;
        }

        if (e) {
            e.stopPropagation();
        }

        setSelectedDateIndex(index);
        if (onDateSelect) {
            onDateSelect(item, index);
        }
    };

    // selectedDate prop이 변경될 때 selectedDateIndex 업데이트
    useEffect(() => {
        if (selectedDate && data && data.length > 0) {
            // selectedDate를 Date 객체로 변환
            const selectedDateObj = selectedDate instanceof Date
                ? selectedDate
                : new Date(selectedDate);

            // selectedDate의 날짜 부분만 추출 (시간 제거)
            const selectedYear = selectedDateObj.getFullYear();
            const selectedMonth = selectedDateObj.getMonth() + 1;
            const selectedDay = selectedDateObj.getDate();

            // data 배열에서 해당 날짜를 찾기
            const foundIndex = data.findIndex(item => {
                if (!item.day) return false;

                let itemDateObj;

                // item.day가 다양한 형식일 수 있음 (yyyy-mm-dd, yyyy/mm/dd, mm/dd 등)
                if (item.day.includes('/')) {
                    // 슬래시(/) 형식 파싱
                    const parts = item.day.split('/');
                    if (parts.length >= 3) {
                        // yyyy/mm/dd 형식
                        itemDateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    } else if (parts.length === 2) {
                        // mm/dd 형식인 경우, 현재 연도 사용
                        itemDateObj = new Date(selectedYear, parseInt(parts[0]) - 1, parseInt(parts[1]));
                    } else {
                        return false;
                    }
                } else if (item.day.includes('-')) {
                    // 대시(-) 형식 파싱 (yyyy-mm-dd)
                    const parts = item.day.split('-');
                    if (parts.length >= 3) {
                        itemDateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }

                // 날짜 비교 (년, 월, 일만 비교)
                return itemDateObj.getFullYear() === selectedYear &&
                    itemDateObj.getMonth() + 1 === selectedMonth &&
                    itemDateObj.getDate() === selectedDay;
            });

            if (foundIndex !== -1) {
                setSelectedDateIndex(foundIndex);
            } else {
                // 찾지 못한 경우 selectedDateIndex를 null로 설정하지 않고 유지
                // (이전 선택이 유지되도록)
            }
        }
    }, [selectedDate, data]);

    // 스크롤 핸들러
    const handleScroll = (e) => {
        const container = e.target;
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;

        // 스크롤 중 플래그 설정
        scrollRef.current.isScrolling = true;

        // 스크롤이 끝난 후 일정 시간 후에 플래그 해제
        clearTimeout(scrollRef.current.scrollTimeout);
        scrollRef.current.scrollTimeout = setTimeout(() => {
            scrollRef.current.isScrolling = false;
            scrollRef.current.scrollEndTime = Date.now();
        }, 150); // 스크롤이 멈춘 후 150ms 후에 플래그 해제

        // 왼쪽 끝에 도달했을 때 (과거 데이터 로드)
        if (scrollLeft <= 0 && onScrollToEnd) {
            onScrollToEnd('past');
        }

        // 오른쪽 끝에 도달했을 때 (미래 데이터 로드)
        if (scrollLeft + clientWidth >= scrollWidth - 1 && onScrollToEnd) {
            onScrollToEnd('future');
        }
    };

    // Y축 라벨 설정 (목표값 기준)    
    const defaultYAxisLabels = [
        targetValue.toLocaleString(), // 목표값
        Math.round(targetValue / 2).toLocaleString(), // 목표값의 50%
        '0'
    ];
    const labels = yAxisLabels.length > 0 ? yAxisLabels : defaultYAxisLabels;

    // 주간 스와이프를 위한 터치 핸들러
    const handleTouchStart = useCallback((e) => {
        if (!enableWeekSwipe || !onWeekChange) {
            console.log('🎯 handleTouchStart: early return', { enableWeekSwipe, onWeekChange: !!onWeekChange });
            return;
        }

        // selectedDate가 없으면 현재 날짜 사용
        const dateToUse = selectedDate || new Date();

        const touch = e.touches[0];
        if (!touch) {
            console.log('🎯 handleTouchStart: no touch, returning');
            return;
        }

        swipeRef.current.startX = touch.clientX;
        swipeRef.current.startY = touch.clientY;
        swipeRef.current.endX = touch.clientX;
        swipeRef.current.endY = touch.clientY;
        swipeRef.current.isVerticalScroll = false;
        swipeRef.current.hasMoved = false;
        swipeRef.current.isClick = false;
        swipeRef.current.dateToUse = dateToUse; // 사용할 날짜 저장

    }, [enableWeekSwipe, onWeekChange, selectedDate]);

    const handleTouchMove = useCallback((e) => {
        if (!enableWeekSwipe) return;

        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = Math.abs(touch.clientX - swipeRef.current.startX);
        const deltaY = Math.abs(touch.clientY - swipeRef.current.startY);

        // 수평 이동이 수직 이동보다 크면 스와이프로 간주하고 기본 동작 방지
        // cancelable이 true일 때만 preventDefault 호출
        if (deltaX > deltaY && deltaX > 10 && e.cancelable) {
            e.preventDefault();
        }

        swipeRef.current.endX = touch.clientX;
        swipeRef.current.endY = touch.clientY;
        swipeRef.current.hasMoved = true;

        // 수직 이동이 수평 이동보다 크면 스크롤로 간주
        if (deltaY > deltaX && deltaY > 10) {
            swipeRef.current.isVerticalScroll = true;
        }
    }, [enableWeekSwipe]);

    const handleTouchEnd = useCallback((e) => {
        if (!enableWeekSwipe || !onWeekChangeRef.current) {
            console.log('🎯 handleTouchEnd: early return', { enableWeekSwipe, onWeekChange: !!onWeekChangeRef.current });
            return;
        }

        // selectedDate가 없으면 저장된 날짜 또는 현재 날짜 사용
        const dateToUse = swipeRef.current.dateToUse || selectedDate || new Date();

        // 터치 종료 시점의 좌표 업데이트
        if (e && e.changedTouches && e.changedTouches[0]) {
            swipeRef.current.endX = e.changedTouches[0].clientX;
            swipeRef.current.endY = e.changedTouches[0].clientY;
        }

        setTimeout(() => {
            if (swipeRef.current.isClick) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            if (swipeRef.current.isVerticalScroll) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            if (!swipeRef.current.hasMoved) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            const deltaX = swipeRef.current.endX - swipeRef.current.startX;
            const deltaY = swipeRef.current.endY - swipeRef.current.startY;
            const threshold = 50;
            const clickThreshold = 10;

            // 클릭으로 간주
            if (Math.abs(deltaX) < clickThreshold && Math.abs(deltaY) < clickThreshold) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            // 수직 이동이 수평 이동보다 크거나 같으면 스크롤로 간주하고 주간 이동하지 않음
            if (Math.abs(deltaY) >= Math.abs(deltaX) * 0.8) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            // 수평 이동이 수직 이동보다 명확히 크고 threshold보다 크면 스와이프로 간주
            if (Math.abs(deltaX) > Math.abs(deltaY) * 1.2 && Math.abs(deltaX) > threshold) {
                if (deltaX > 0) {
                    // 오른쪽 스와이프 (이전 주로 이동, 즉 과거로 이동)
                    const newDate = new Date(dateToUse);
                    newDate.setDate(dateToUse.getDate() - 7);
                    newDate.setHours(0, 0, 0, 0);
                    onWeekChangeRef.current(newDate, -1);
                } else {
                    // 왼쪽 스와이프 (다음 주로 이동, 즉 미래로 이동)
                    const newDate = new Date(dateToUse);
                    newDate.setDate(dateToUse.getDate() + 7);
                    newDate.setHours(0, 0, 0, 0);
                    onWeekChangeRef.current(newDate, 1);
                }
            } else {
                console.log('🎯 handleTouchEnd: not a swipe', {
                    deltaX,
                    deltaY,
                    threshold,
                    ratio: Math.abs(deltaX) / Math.abs(deltaY),
                    condition1: Math.abs(deltaX) > Math.abs(deltaY) * 1.2,
                    condition2: Math.abs(deltaX) > threshold
                });
            }

            // 리셋
            swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0 };
        }, 50);
    }, [enableWeekSwipe, selectedDate]);

    // handleTouchStart, handleTouchMove, handleTouchEnd ref 업데이트
    useEffect(() => {
        handleTouchStartRef.current = handleTouchStart;
        handleTouchMoveRef.current = handleTouchMove;
        handleTouchEndRef.current = handleTouchEnd;
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    // 마우스 드래그 이벤트 핸들러 (데스크톱용)
    const handleMouseDown = useCallback((e) => {
        if (!enableWeekSwipe || !onWeekChange) {
            console.log('handleMouseDown: early return', { enableWeekSwipe, onWeekChange: !!onWeekChange });
            return;
        }

        // selectedDate가 없으면 현재 날짜 사용
        const dateToUse = selectedDate || new Date();
        swipeRef.current.dateToUse = dateToUse; // 사용할 날짜 저장

        // 바 요소에서 발생한 클릭인지 확인
        const target = e.target;
        const isBarClick = target.closest('[data-bar-clickable="true"]');

        if (isBarClick) {
            // 바 클릭인 경우에도 스와이프를 위해 초기 좌표 저장 (나중에 이동 거리로 클릭/스와이프 구분)
            swipeRef.current.isMouseDown = true;
            swipeRef.current.startX = e.clientX;
            swipeRef.current.startY = e.clientY;
            swipeRef.current.endX = e.clientX;
            swipeRef.current.endY = e.clientY;
            swipeRef.current.hasMoved = false;
            swipeRef.current.isClick = false;
            swipeRef.current.isBarClick = true; // 바 클릭 플래그
            return;
        }

        // 바가 아닌 영역에서 스와이프 시작
        swipeRef.current.isMouseDown = true;
        swipeRef.current.startX = e.clientX;
        swipeRef.current.startY = e.clientY;
        swipeRef.current.endX = e.clientX;
        swipeRef.current.endY = e.clientY;
        swipeRef.current.hasMoved = false;
        swipeRef.current.isClick = false;
        swipeRef.current.isBarClick = false;
    }, [enableWeekSwipe, onWeekChange, selectedDate]);

    // ref에 핸들러 저장
    handleMouseDownRef.current = handleMouseDown;

    const handleMouseMove = useCallback((e) => {
        if (!enableWeekSwipe || !swipeRef.current.isMouseDown) {
            console.log('handleMouseMove: early return', { enableWeekSwipe, isMouseDown: swipeRef.current.isMouseDown });
            return;
        }

        swipeRef.current.endX = e.clientX;
        swipeRef.current.endY = e.clientY;
        swipeRef.current.hasMoved = true;
    }, [enableWeekSwipe]);

    // ref에 핸들러 저장
    handleMouseMoveRef.current = handleMouseMove;

    const handleMouseUp = useCallback((e) => {
        if (!enableWeekSwipe || !onWeekChangeRef.current || !swipeRef.current.isMouseDown) {
            console.log('handleMouseUp: early return', { enableWeekSwipe, onWeekChange: !!onWeekChangeRef.current, isMouseDown: swipeRef.current.isMouseDown });
            swipeRef.current.isMouseDown = false;
            swipeRef.current.isBarClick = false;
            return;
        }

        // selectedDate가 없으면 저장된 날짜 또는 현재 날짜 사용
        const dateToUse = swipeRef.current.dateToUse || selectedDate || new Date();

        swipeRef.current.endX = e.clientX;
        swipeRef.current.endY = e.clientY;
        swipeRef.current.isMouseDown = false;

        setTimeout(() => {
            if (swipeRef.current.isClick) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            if (!swipeRef.current.hasMoved) {
                // 이동하지 않았고 바 클릭이었다면 클릭으로 처리 (handleBarClick에서 처리됨)
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            const deltaX = swipeRef.current.endX - swipeRef.current.startX;
            const deltaY = Math.abs(swipeRef.current.endY - swipeRef.current.startY);
            const threshold = 50;
            const clickThreshold = 10;

            // 클릭으로 간주 (이동 거리가 작으면)
            if (Math.abs(deltaX) < clickThreshold && deltaY < clickThreshold) {
                // 바 클릭이었다면 클릭으로 처리 (handleBarClick에서 처리됨)
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            // 수직 이동이 수평 이동보다 크거나 같으면 스크롤로 간주하고 주간 이동하지 않음
            if (deltaY >= Math.abs(deltaX) * 0.8) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                return;
            }

            // 수평 이동이 수직 이동보다 명확히 크고 threshold보다 크면 스와이프로 간주
            if (Math.abs(deltaX) > deltaY * 1.2 && Math.abs(deltaX) > threshold) {
                // 바에서 시작했더라도 스와이프로 간주하고 주간 이동 처리
                if (deltaX > 0) {
                    // 오른쪽 드래그 (이전 주로 이동, 즉 과거로 이동)
                    const newDate = new Date(dateToUse);
                    newDate.setDate(dateToUse.getDate() - 7);
                    newDate.setHours(0, 0, 0, 0);
                    onWeekChangeRef.current(newDate, -1);
                } else {
                    // 왼쪽 드래그 (다음 주로 이동, 즉 미래로 이동)
                    const newDate = new Date(dateToUse);
                    newDate.setDate(dateToUse.getDate() + 7);
                    newDate.setHours(0, 0, 0, 0);
                    console.log('handleMouseUp: calling onWeekChange (next week)', { newDate, delta: 1 });
                    onWeekChangeRef.current(newDate, 1);
                }
            } else {
                console.log('handleMouseUp: not a swipe', { deltaX, deltaY, threshold, ratio: deltaY > 0 ? Math.abs(deltaX) / deltaY : 'infinity' });
            }

            // 리셋
            swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
        }, 50);
    }, [enableWeekSwipe, selectedDate]);

    // ref에 핸들러 저장
    handleMouseUpRef.current = handleMouseUp;

    // 전역 마우스 이벤트 처리
    useEffect(() => {
        if (!enableWeekSwipe) {
            console.log('Global mouse event listeners: enableWeekSwipe is false, returning');
            return;
        }

        if (!onWeekChangeRef.current) {
            console.log('Global mouse event listeners: onWeekChange is null, returning');
            return;
        }

        const handleGlobalMouseDown = (e) => {
            // 이벤트가 발생한 위치 확인
            const isInContainer = containerRef.current && containerRef.current.contains(e.target);
            const isInScrollContainer = scrollContainerRef.current && scrollContainerRef.current.contains(e.target);

            // e.target이 Element인 경우에만 closest 메서드 사용
            let isWeeklyChartElement = isInContainer || isInScrollContainer;
            if (e.target && typeof e.target.closest === 'function') {
                const closestWeeklyChart = e.target.closest('[data-weekly-chart]');
                if (closestWeeklyChart) {
                    isWeeklyChartElement = true;
                }
            }

            // WeeklyChart 컨테이너 내부에서 발생한 이벤트인지 확인
            // containerRef 또는 scrollContainerRef 내부에서 발생한 이벤트도 처리
            // 또는 이벤트가 WeeklyChart의 자식 요소에서 발생한 경우도 처리
            if (isWeeklyChartElement) {
                // ref를 통해 최신 핸들러 호출
                if (handleMouseDownRef.current) {
                    handleMouseDownRef.current(e);
                }
            } else {
                console.log('❌ Global mousedown NOT on WeeklyChart', {
                    containerRef: !!containerRef.current,
                    scrollContainerRef: !!scrollContainerRef.current,
                    target: e.target,
                    targetTagName: e.target?.tagName,
                    targetClassName: e.target?.className,
                    isInContainer,
                    isInScrollContainer
                });
            }
        };

        const handleGlobalMouseMove = (e) => {
            if (swipeRef.current.isMouseDown) {
                // ref를 통해 최신 핸들러 호출
                if (handleMouseMoveRef.current) {
                    handleMouseMoveRef.current(e);
                }
            }
        };

        const handleGlobalMouseUp = (e) => {
            if (swipeRef.current.isMouseDown) {
                // ref를 통해 최신 핸들러 호출
                if (handleMouseUpRef.current) {
                    handleMouseUpRef.current(e);
                }
            }
        };

        // document 레벨에서 capture phase와 bubble phase 모두에서 이벤트 리스너 추가
        // capture phase에서 먼저 처리하여 다른 리스너보다 우선순위를 높임
        document.addEventListener('mousedown', handleGlobalMouseDown, true);
        document.addEventListener('mousemove', handleGlobalMouseMove, true);
        document.addEventListener('mouseup', handleGlobalMouseUp, true);

        // bubble phase에서도 처리 (capture phase에서 차단되지 않은 경우)
        document.addEventListener('mousedown', handleGlobalMouseDown, false);
        document.addEventListener('mousemove', handleGlobalMouseMove, false);
        document.addEventListener('mouseup', handleGlobalMouseUp, false);

        // 이벤트 리스너가 실제로 등록되었는지 확인
        const testEvent = new MouseEvent('mousedown', { bubbles: true, cancelable: true });

        // 실제 이벤트를 발생시켜서 리스너가 작동하는지 확인
        setTimeout(() => {
            document.dispatchEvent(testEvent);
        }, 100);

        return () => {
            document.removeEventListener('mousedown', handleGlobalMouseDown, true);
            document.removeEventListener('mousemove', handleGlobalMouseMove, true);
            document.removeEventListener('mouseup', handleGlobalMouseUp, true);
            document.removeEventListener('mousedown', handleGlobalMouseDown, false);
            document.removeEventListener('mousemove', handleGlobalMouseMove, false);
            document.removeEventListener('mouseup', handleGlobalMouseUp, false);
        };
    }, [enableWeekSwipe]); // 의존성 배열에서 handleMouseDown, handleMouseMove, handleMouseUp 제거 (ref 사용)

    // 터치 이벤트 리스너 추가 (scrollContainerRef와 containerRef 모두에 추가)
    useEffect(() => {
        if (!enableWeekSwipe) return;

        const touchStartHandler = (e) => {
            // 바 요소에서 발생한 터치인지 확인
            const target = e.target;
            const isBarClick = target.closest('[data-bar-clickable="true"]');
            if (isBarClick) {
                // 바에서 터치가 시작되었지만, 이동 거리가 크면 스와이프로 처리하기 위해 초기 좌표 저장
                swipeRef.current.touchStartX = e.touches[0]?.clientX || 0;
                swipeRef.current.touchStartY = e.touches[0]?.clientY || 0;
                swipeRef.current.touchStartTime = Date.now();
                swipeRef.current.isClick = true; // 일단 클릭으로 가정, touchMove에서 이동 거리 확인 후 변경

                // 스와이프를 위해 초기 좌표도 저장
                swipeRef.current.startX = e.touches[0]?.clientX || 0;
                swipeRef.current.startY = e.touches[0]?.clientY || 0;
                swipeRef.current.endX = e.touches[0]?.clientX || 0;
                swipeRef.current.endY = e.touches[0]?.clientY || 0;
                swipeRef.current.isVerticalScroll = false;
                swipeRef.current.hasMoved = false;

                // selectedDate가 없으면 현재 날짜 사용
                const dateToUse = selectedDate || new Date();
                swipeRef.current.dateToUse = dateToUse;

                return;
            }

            if (e.cancelable) {
                e.preventDefault();
            }
            if (handleTouchStartRef.current) {
                handleTouchStartRef.current(e);
            }
        };

        const touchMoveHandler = (e) => {
            // 바 요소에서 발생한 터치인지 확인
            const target = e.target;
            const isBarClick = target.closest('[data-bar-clickable="true"]');

            if (isBarClick && swipeRef.current.isClick) {
                // 바에서 터치가 이동하는 경우, 이동 거리를 확인하여 스와이프로 전환
                const touch = e.touches[0];
                if (touch) {
                    const deltaX = Math.abs(touch.clientX - swipeRef.current.touchStartX);
                    const deltaY = Math.abs(touch.clientY - swipeRef.current.touchStartY);

                    // 이동 거리가 크면 스와이프로 처리
                    if (deltaX > 10 || deltaY > 10) {
                        // 스와이프로 전환
                        swipeRef.current.isClick = false;
                        // startX/Y를 touchStartX/Y로 설정
                        swipeRef.current.startX = swipeRef.current.touchStartX;
                        swipeRef.current.startY = swipeRef.current.touchStartY;
                        swipeRef.current.endX = touch.clientX;
                        swipeRef.current.endY = touch.clientY;
                        swipeRef.current.hasMoved = true;
                    } else {
                        // 이동 거리가 작으면 클릭으로 유지
                        console.log('🎯 touchMoveHandler: movement too small, keeping as click');
                        return;
                    }
                }
            }

            if (e.cancelable) {
                e.preventDefault();
            }
            if (handleTouchMoveRef.current) {
                handleTouchMoveRef.current(e);
            }
        };

        const touchEndHandler = (e) => {
            // 바 요소에서 발생한 터치인지 확인
            const target = e.target;
            const isBarClick = target.closest('[data-bar-clickable="true"]');

            if (isBarClick && swipeRef.current.isClick && swipeRef.current.touchStartTime) {
                // 바 클릭 처리
                const touch = e.changedTouches?.[0];
                if (touch) {
                    const deltaX = Math.abs(touch.clientX - swipeRef.current.touchStartX);
                    const deltaY = Math.abs(touch.clientY - swipeRef.current.touchStartY);
                    const deltaTime = Date.now() - swipeRef.current.touchStartTime;

                    // 이동 거리가 작고 시간이 짧으면 클릭으로 간주
                    if (deltaX < 10 && deltaY < 10 && deltaTime < 500) {
                        // 바 클릭 이벤트 트리거
                        const barIndex = parseInt(target.closest('[data-bar-index]')?.getAttribute('data-bar-index') || '-1');
                        if (barIndex >= 0 && data[barIndex]) {
                            handleBarClick(barIndex, data[barIndex], e);
                        }
                        swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, isBarClick: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0, dateToUse: null };
                        return;
                    }
                }
            }

            if (e.cancelable) {
                e.preventDefault();
            }
            if (handleTouchEndRef.current) {
                handleTouchEndRef.current(e);
            }
        };

        // scrollContainerRef와 containerRef 모두에 이벤트 리스너 추가
        const scrollElement = scrollContainerRef.current;
        const containerElement = containerRef.current;

        if (scrollElement) {
            scrollElement.addEventListener('touchstart', touchStartHandler, { passive: false });
            scrollElement.addEventListener('touchmove', touchMoveHandler, { passive: false });
            scrollElement.addEventListener('touchend', touchEndHandler, { passive: false });
        }

        if (containerElement) {
            containerElement.addEventListener('touchstart', touchStartHandler, { passive: false });
            containerElement.addEventListener('touchmove', touchMoveHandler, { passive: false });
            containerElement.addEventListener('touchend', touchEndHandler, { passive: false });
        }

        return () => {
            if (scrollElement) {
                scrollElement.removeEventListener('touchstart', touchStartHandler);
                scrollElement.removeEventListener('touchmove', touchMoveHandler);
                scrollElement.removeEventListener('touchend', touchEndHandler);
            }
            if (containerElement) {
                containerElement.removeEventListener('touchstart', touchStartHandler);
                containerElement.removeEventListener('touchmove', touchMoveHandler);
                containerElement.removeEventListener('touchend', touchEndHandler);
            }
        };
    }, [enableWeekSwipe]); // handleTouchStart, handleTouchMove, handleTouchEnd는 ref로 관리하므로 의존성에서 제거

    // 초기 스크롤 위치 설정 및 내부 콘텐츠 너비 설정
    useLayoutEffect(() => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const itemWidth = barWidth + gap;
            const totalContentWidth = data.length * itemWidth - gap;

            // 스크롤 컨테이너의 실제 콘텐츠 너비를 설정하여 모든 데이터가 스크롤 가능하도록 함
            if (data.length > visibleItems) {
                // DOM이 렌더링된 후 실제 너비 확인 및 조정
                const adjustScrollWidth = () => {
                    if (!container) return;

                    // 실제 스크롤 너비 확인
                    const currentScrollWidth = container.scrollWidth;
                    const expectedScrollWidth = totalContentWidth;

                    // 스크롤 너비가 예상보다 작으면 조정
                    if (currentScrollWidth < expectedScrollWidth) {
                        // 각 바 요소의 너비가 제대로 설정되었는지 확인
                        const bars = container.querySelectorAll('[data-bar-clickable="true"]');
                        if (bars.length > 0) {
                            // 각 바의 너비 확인 및 조정
                            bars.forEach(bar => {
                                const computedStyle = window.getComputedStyle(bar);
                                const currentWidth = parseFloat(computedStyle.width) || 0;
                                if (currentWidth < itemWidth) {
                                    bar.style.width = `${itemWidth}px`;
                                    bar.style.minWidth = `${itemWidth}px`;
                                }
                            });
                        }

                        // 컨테이너 자체의 너비를 조정하여 스크롤 가능하도록 함
                        // 하지만 width는 visibleItems만큼 유지하고, 내부 콘텐츠만 확장
                        // 실제로는 각 바가 flexShrink: 0이므로 자동으로 확장되어야 함
                        // 만약 여전히 작동하지 않으면, 컨테이너에 minWidth를 설정
                        if (container.scrollWidth < expectedScrollWidth) {
                            // 내부 콘텐츠의 실제 너비를 보장하기 위해 컨테이너의 minWidth 설정
                            // 하지만 이렇게 하면 컨테이너 자체가 확장될 수 있으므로 주의
                            // 대신 각 바의 너비를 명시적으로 설정
                        }
                    }
                };

                // 여러 번 시도하여 DOM이 완전히 렌더링될 때까지 기다림
                requestAnimationFrame(() => {
                    adjustScrollWidth();
                    setTimeout(adjustScrollWidth, 0);
                    setTimeout(adjustScrollWidth, 50);
                    setTimeout(adjustScrollWidth, 100);
                });
            }

            let scrollPosition = 0;
            if (startFromEnd && data.length > visibleItems) {
                // 끝에서부터 시작
                const startIndex = data.length - visibleItems;
                scrollPosition = startIndex * itemWidth;
            }

            // DOM이 완전히 렌더링된 후 스크롤 설정
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (container) {
                        container.scrollLeft = scrollPosition;
                    }
                }, 0);
            });
        }
    }, [data.length, barWidth, gap, visibleItems, startFromEnd]);

    // 직접 DOM 이벤트 리스너 추가 (heartbeat.js에서 작동하지 않는 문제 해결)
    useLayoutEffect(() => {
        if (!enableWeekSwipe) {
            console.log('enableWeekSwipe is false, skipping event listener setup');
            return;
        }

        let cleanupFn = null;

        // DOM이 완전히 렌더링될 때까지 대기
        const timeoutId = setTimeout(() => {
            if (!containerRef.current) {
                console.log('containerRef.current is null after timeout');
                return;
            }

            const element = containerRef.current;

            const mouseDownHandler = (e) => {
                // ref를 통해 최신 핸들러 호출
                if (handleMouseDownRef.current) {
                    handleMouseDownRef.current(e);
                }
            };

            const mouseMoveHandler = (e) => {
                if (swipeRef.current.isMouseDown && handleMouseMoveRef.current) {
                    handleMouseMoveRef.current(e);
                }
            };

            const mouseUpHandler = (e) => {
                if (swipeRef.current.isMouseDown && handleMouseUpRef.current) {
                    handleMouseUpRef.current(e);
                }
            };

            // 캡처 단계에서 이벤트 리스너 추가 (가장 먼저 처리되도록)
            element.addEventListener('mousedown', mouseDownHandler, { capture: true, passive: false });
            element.addEventListener('mousemove', mouseMoveHandler, { capture: true, passive: false });
            element.addEventListener('mouseup', mouseUpHandler, { capture: true, passive: false });

            // 버블링 단계에서도 추가 (이중 보험)
            element.addEventListener('mousedown', mouseDownHandler, { capture: false, passive: false });
            element.addEventListener('mousemove', mouseMoveHandler, { capture: false, passive: false });
            element.addEventListener('mouseup', mouseUpHandler, { capture: false, passive: false });

            // cleanup 함수 저장
            cleanupFn = () => {
                element.removeEventListener('mousedown', mouseDownHandler, { capture: true });
                element.removeEventListener('mousemove', mouseMoveHandler, { capture: true });
                element.removeEventListener('mouseup', mouseUpHandler, { capture: true });
                element.removeEventListener('mousedown', mouseDownHandler, { capture: false });
                element.removeEventListener('mousemove', mouseMoveHandler, { capture: false });
                element.removeEventListener('mouseup', mouseUpHandler, { capture: false });
            };
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            if (cleanupFn) {
                cleanupFn();
            }
        };
    }, [enableWeekSwipe]); // 의존성 배열에서 handleMouseDown, handleMouseMove, handleMouseUp 제거 (ref 사용)

    // Y축 라벨과 그리드 위치 계산 (화면 너비 변경에 대응)
    useLayoutEffect(() => {
        const updateYAxisPosition = () => {
            if (!scrollContainerRef.current || !containerRef.current) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const scrollContainerRect = scrollContainerRef.current.getBoundingClientRect();

            // 바 차트의 실제 너비와 오른쪽 끝 위치 (컨테이너 기준)
            const chartWidth = scrollContainerRect.width;
            const chartRightRelativeToContainer = scrollContainerRect.right - containerRect.left;
            const chartLeftRelativeToContainer = scrollContainerRect.left - containerRect.left;

            // 그리드 라인의 너비를 실제 바 차트의 너비로 설정
            setGridLineWidth(chartWidth);

            // 그리드 라인의 오른쪽 끝이 바 차트의 오른쪽 끝과 일치해야 함
            // 그리드 라인의 오른쪽 끝 = Y축 라벨 컨테이너의 왼쪽 끝 - 8px
            // Y축 라벨 컨테이너의 왼쪽 끝 = containerRect.width - yAxisRight - 40px
            // 따라서: containerRect.width - yAxisRight - 40px - 8px = chartRightRelativeToContainer
            //        yAxisRight = containerRect.width - 40px - 8px - chartRightRelativeToContainer

            // Y축 라벨이 그리드와 바 차트의 우측에 오도록 간격 추가 (8px)
            const labelGap = 8;
            const containerWidth = containerRect.width;
            const rightPosition = containerWidth - 40 - labelGap - chartRightRelativeToContainer;
            setYAxisRight(Math.max(0, rightPosition)); // 음수 방지
        };

        // DOM이 완전히 렌더링된 후 위치 계산
        const timeoutId = setTimeout(() => {
            updateYAxisPosition();
        }, 100); // 약간의 지연을 두어 레이아웃이 완전히 완료되도록 함

        // ResizeObserver로 컨테이너 크기 변경 감지
        const resizeObserver = new ResizeObserver(() => {
            // 약간의 지연을 두어 레이아웃이 완전히 완료되도록 함
            setTimeout(() => {
                updateYAxisPosition();
            }, 10);
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        if (scrollContainerRef.current) {
            resizeObserver.observe(scrollContainerRef.current);
        }

        // 윈도우 리사이즈 이벤트도 감지
        const handleResize = () => {
            setTimeout(() => {
                updateYAxisPosition();
            }, 10);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [data.length, visibleItems, barWidth, gap]);

    return (
        <Box
            ref={containerRef}
            data-weekly-chart="true"
            sx={{
                display: 'flex',
                alignItems: 'end',
                width: '100%',
                position: 'relative',
                minHeight: `${height + 40}px`, // 최소 높이 설정 (차트 높이 + 여백)
                touchAction: enableWeekSwipe ? 'pan-y pan-x' : 'auto', // 수직 스크롤과 수평 스와이프 모두 허용
                pointerEvents: 'auto', // 포인터 이벤트 활성화
                zIndex: enableWeekSwipe ? 10 : 'auto', // 스와이프 활성화 시 z-index를 높여서 다른 요소 위에 표시
                justifyContent: 'center', // 중앙 정렬
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                ...sx
            }}
            onMouseDown={enableWeekSwipe ? (e) => {
                // stopPropagation 제거 - document 레벨 이벤트 리스너가 먼저 처리하도록 함
                // ref를 통해 최신 핸들러 호출
                if (handleMouseDownRef.current) {
                    handleMouseDownRef.current(e);
                }
            } : handleDragScrollMouseDown}
            onMouseMove={enableWeekSwipe ? (e) => {
                if (swipeRef.current.isMouseDown) {
                    handleMouseMove(e);
                }
            } : undefined}
            {...props}
        >
            {/* Y축 라벨과 그리드 */}
            {showYAxis && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    width: '40px',
                    position: 'absolute',
                    right: `${yAxisRight}px`,
                    bottom: '12px',
                    zIndex: 1,
                    paddingBottom: '13px',
                    pointerEvents: 'none'
                }}>
                    {labels.map((label, index) => {
                        return (
                            <Box key={index} sx={{
                                height: '12px',
                                position: 'relative',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                                {/* 그리드 라인 */}
                                {label !== '' && (
                                    <Box sx={{
                                        position: 'absolute',
                                        right: `${40 + 8}px`, // Y축 라벨(40px) + 간격(8px)
                                        width: `${gridLineWidth}px`,
                                        height: '1px',
                                        borderTop: index > 0 && index < labels.length - 1 && !isRangeData ? `1px dashed ${theme.palette.figma.white[30]}` : 'none',
                                        backgroundColor: index > 0 && index < labels.length - 1 && !isRangeData ? 'transparent' : (index === 0 ? theme.palette.figma.stepsDark : theme.palette.figma.white[30]),
                                        zIndex: 0
                                    }} />
                                )}
                                {/* Y축 라벨 - 그리드의 오른쪽 끝에서 8px 간격을 두고 배치 */}
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        color: targetValue === label ? barColor : theme.palette.figma.white[60],
                                        fontWeight: index === 0 ? 500 : 400,
                                        letterSpacing: '-0.3px',
                                        transform: isRangeData ? 'none' : 'translateY(-50%)',
                                        width: '40px',
                                        textAlign: 'left',
                                        position: 'absolute',
                                        left: '0px', // Y축 라벨 컨테이너의 왼쪽 끝에 위치
                                    }}
                                >
                                    {label.toLocaleString()}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* 바 차트 */}
            <Box
                ref={scrollContainerRef}
                onScroll={handleScroll}
                style={enableWeekSwipe ? {
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    cursor: 'grab',
                    touchAction: 'pan-y',
                    pointerEvents: 'auto' // 포인터 이벤트 활성화
                } : {
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    cursor: isDragScrolling ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                }}
                sx={{
                    display: 'flex',
                    overflowX: 'auto', // 항상 스크롤 활성화
                    overflowY: 'hidden',
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE/Edge
                    '&::-webkit-scrollbar': { // WebKit
                        display: 'none'
                    },
                    // 스크롤 컨테이너의 너비는 보이는 영역만큼만 (visibleItems)
                    // 하지만 내부 콘텐츠는 모든 데이터를 포함하므로 스크롤 가능
                    width: data.length >= visibleItems
                        ? `${visibleItems * (barWidth + gap)}px` // 데이터가 많을 때: visibleItems만큼만 표시
                        : `${data.length * (barWidth + gap) - gap}px`, // 데이터가 적을 때: 실제 데이터 길이만큼만 표시
                    // maxWidth는 컨테이너 너비를 제한하되, 내부 콘텐츠는 확장 가능
                    // 내부 래퍼 Box가 명시적 width를 가지므로 스크롤 가능
                    maxWidth: data.length >= visibleItems
                        ? `${visibleItems * (barWidth + gap)}px`
                        : `${data.length * (barWidth + gap) - gap}px`,
                    minWidth: '200px', // 최소 너비 설정
                    minHeight: `${height + 30}px`, // 최소 높이 설정 (차트 높이 + 라벨 높이)
                    marginRight: data.length >= visibleItems ? `30px` : '0px', // 스크롤이 없을 때는 패딩 제거
                    justifyContent: data.length <= visibleItems ? 'center' : 'flex-start', // 데이터가 적을 때 중앙 정렬
                }}
            >
                {/* 내부 콘텐츠 래퍼 - 모든 데이터를 포함하는 너비 설정 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexShrink: 0,
                        width: `${data.length * (barWidth + gap) - gap}px`, // 모든 데이터를 포함하는 명시적 너비
                        minWidth: `${data.length * (barWidth + gap) - gap}px`, // 최소 너비도 설정
                    }}
                >
                    {data && data.length > 0 ? data.map((item, index) => {
                        // 심박수 범위 데이터 지원: max 값이 있으면 max 사용, 없으면 value 사용
                        const value = item.max || item.value || item.steps || 0;
                        const isCurrent = (selectedDateIndex != null ? selectedDateIndex === index : item.isCurrent); // 클릭된 바가 현재 선택된 바
                        const label = item.day || '';
                        const itemColor = item.color || barColor; // 개별 바 색상 지원

                        // 라벨 포맷팅: language 설정(locale)에 맞는 날짜 표기 사용
                        let formattedLabel = '';
                        if (label) {
                            let dateObj = null;
                            if (label.includes('/')) {
                                const parts = label.split('/').map(p => parseInt(p, 10));
                                if (parts.length >= 3) dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                            } else if (label.includes('-')) {
                                const parts = label.split('-').map(p => parseInt(p, 10));
                                if (parts.length >= 3) dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                            }
                            formattedLabel = dateObj && !Number.isNaN(dateObj.getTime())
                                ? formatDateTimeDisplay(dateObj, 'monthDay')
                                : label;
                        }

                        // 바 높이 계산 - 심박수 범위 기준
                        let barHeight, barBottom;
                        if (isRangeData) {
                            // 현재 바의 min, max 값
                            const barMin = item.min || 0;
                            const barMax = item.max || 0;

                            barHeight = chartHeight / (rangeDataMax - rangeDataMin) * (barMax - barMin);
                            barBottom = chartHeight / (rangeDataMax - rangeDataMin) * (barMin - 40) + 4;
                        } else {
                            // 기존 방식 (목표값 기준)
                            barHeight = (value / targetValue) * chartHeight;
                            barBottom = chartHeight - barHeight;
                        }

                        return (
                            <Box
                                key={index}
                                data-bar-clickable="true"
                                data-bar-index={index}
                                onClick={(e) => {
                                    // 클릭이 확실할 때만 처리 (스와이프가 아닌 경우)
                                    if (dragScrollRef.current.moved) {
                                        return;
                                    }
                                    if (!swipeRef.current.hasMoved ||
                                        (Math.abs(swipeRef.current.endX - swipeRef.current.startX) < 10 &&
                                            Math.abs(swipeRef.current.endY - swipeRef.current.startY) < 10)) {
                                        handleBarClick(index, item, e);
                                    }
                                }}
                                onMouseDown={(e) => {
                                    // 바 클릭인 경우에도 스와이프를 위해 이벤트 전파 허용
                                    // stopPropagation을 호출하지 않아서 컨테이너의 handleMouseDown도 호출됨
                                }}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '5px',
                                    width: `${barWidth + gap}px`, // 고정 너비로 변경
                                    minWidth: `${barWidth + gap}px`, // 최소 너비 보장
                                    flexShrink: 0, // 축소 방지
                                    flexGrow: 0, // 확장 방지
                                    padding: '10px 0',
                                    borderRadius: '10px',
                                    backgroundColor: isCurrent ? theme.palette.figma.block : 'transparent',
                                    boxShadow: isCurrent ? (theme.palette.shadows?.elevation?.[2] ?? 'none') : 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: isCurrent ? theme.palette.figma.block : theme.palette.figma.white[10],
                                    }
                                }}>
                                <Box sx={{
                                    height: `${height}px`,
                                    width: '100%',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'end',
                                    justifyContent: 'center',
                                }}>
                                    <Box sx={{
                                        height: `${barHeight}px`, // 계산된 높이 사용                                   
                                        minWidth: `${barWidth}px`, // 최소 너비 보장                                    
                                        backgroundColor: itemColor, // 개별 바 색상 사용
                                        borderRadius: '4px',
                                        position: 'absolute',
                                        bottom: isRangeData ? `${barBottom}px` : '-8px', // 심박수 범위는 계산된 위치, 일반 차트는 고정 위치
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        minHeight: '0px' // 최소 높이 보장
                                    }} />
                                </Box>
                                <Typography sx={{
                                    fontSize: '12px',
                                    color: isCurrent ? color : theme.palette.figma.white[60], // 현재 날짜는 바 색상 사용
                                    fontWeight: isCurrent ? 500 : 400,
                                    letterSpacing: '-0.36px',
                                    textAlign: 'center',
                                    width: '100%'
                                }}>
                                    {formattedLabel || label}
                                </Typography>
                            </Box>
                        );
                    }) : (
                        // 데이터가 없을 때 최소 높이를 유지하기 위한 빈 공간
                        <Box sx={{
                            width: `${visibleItems * (barWidth + gap)}px`,
                            minHeight: `${height + 30}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Typography sx={{
                                fontSize: '12px',
                                color: theme.palette.figma.white[60],
                                opacity: 0.5
                            }}>
                                데이터 없음
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default WeeklyChart;
